(() => {
  const instances = new WeakMap();
  const priority = [1, 9, 24, 4, 28, 34, 19, 43, 11, 16, 6, 7];
  const priorityOrder = new Map(priority.map((id, index) => [id, index]));

  function initialize(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const listen = (target, event, handler, options = {}) => target.addEventListener(event, handler, { ...options, signal });
    const get = (selector) => root.querySelector(selector);
    let text = {};
    let photos = [];
    try {
      text = JSON.parse(get('[data-olcp-i18n]').textContent);
      photos = JSON.parse(get('[data-olcp-photos]').textContent)
        .filter((photo) => photo.id && photo.title && (photo.full || photo.url || photo.src))
        .map((photo) => ({
          ...photo,
          id: Number(photo.id),
          src: photo.src || photo.url || photo.full,
          full: photo.full || photo.url || photo.src
        }))
        .sort((a, b) => (priorityOrder.get(a.id) ?? priority.length) - (priorityOrder.get(b.id) ?? priority.length) || a.id - b.id);
    } catch (error) {
      get('[data-gallery-more]').hidden = true;
      root.querySelectorAll('[data-category]').forEach((button) => { button.hidden = true; });
      return;
    }

    const wall = get('[data-gallery-wall]');
    const more = get('[data-gallery-more]');
    const end = get('[data-gallery-end]');
    const count = get('[data-gallery-count]');
    const dialog = get('[data-gallery-dialog]');
    const closeButton = get('[data-dialog-close]');
    const image = get('[data-dialog-image]');
    const title = get('[data-dialog-title]');
    const categoryTitle = get('[data-dialog-category]');
    const position = get('[data-dialog-position]');
    const previous = get('[data-dialog-previous]');
    const next = get('[data-dialog-next]');
    let category = 'all';
    let visibleCount = 6;
    let selectedIndex = -1;
    let trigger = null;
    let previousOverflow = null;

    const categoryLabel = (value) => text.categories[value] || text.categories.all;
    const filtered = () => photos.filter((photo) => category === 'all' || photo.category === category);

    function createCard(photo) {
      const figure = document.createElement('figure');
      figure.className = 'olcp-card';
      const link = document.createElement('a');
      link.className = 'olcp-photo-button';
      link.href = photo.full;
      link.dataset.photoId = photo.id;
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-label', text.view + ': ' + photo.title);
      const thumbnail = document.createElement('img');
      thumbnail.className = 'olcp-photo';
      thumbnail.src = photo.src;
      thumbnail.alt = photo.title;
      thumbnail.width = photo.width;
      thumbnail.height = photo.height;
      thumbnail.loading = 'lazy';
      thumbnail.decoding = 'async';
      const viewLabel = document.createElement('span');
      viewLabel.className = 'olcp-view-label';
      viewLabel.setAttribute('aria-hidden', 'true');
      viewLabel.textContent = text.view;
      link.append(thumbnail, viewLabel);
      const caption = document.createElement('figcaption');
      caption.className = 'olcp-caption';
      const description = document.createElement('span');
      description.textContent = photo.title;
      const categorySpan = document.createElement('span');
      categorySpan.className = 'olcp-category-label';
      categorySpan.textContent = categoryLabel(photo.category);
      caption.append(description, categorySpan);
      figure.append(link, caption);
      return figure;
    }

    function renderGallery() {
      const matching = filtered();
      const shown = matching.slice(0, visibleCount);
      wall.replaceChildren(...shown.map(createCard));
      count.textContent = text.showing.replace('{shown}', shown.length).replace('{total}', matching.length);
      more.hidden = shown.length >= matching.length;
      end.hidden = !more.hidden;
      root.querySelectorAll('[data-category]').forEach((button) => {
        const active = button.dataset.category === category;
        button.classList.toggle('olcp-filter-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    function updateDialog() {
      const matching = filtered();
      const photo = matching[selectedIndex];
      if (!photo) return;
      image.src = photo.full;
      image.alt = photo.title;
      image.width = photo.width;
      image.height = photo.height;
      title.textContent = photo.title;
      categoryTitle.textContent = categoryLabel(photo.category);
      position.textContent = (selectedIndex + 1) + ' / ' + matching.length;
      previous.disabled = next.disabled = matching.length < 2;
    }

    function restoreDialog() {
      selectedIndex = -1;
      if (previousOverflow !== null) {
        document.body.style.overflow = previousOverflow;
        previousOverflow = null;
      }
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      trigger = null;
    }

    function closeDialog() {
      if (dialog.open) dialog.close();
      else restoreDialog();
    }

    function openDialog(link) {
      if (typeof dialog.showModal !== 'function') return false;
      const matching = filtered();
      selectedIndex = matching.findIndex((photo) => photo.id === Number(link.dataset.photoId));
      if (selectedIndex < 0) return false;
      trigger = link;
      updateDialog();
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      try {
        dialog.showModal();
        closeButton.focus();
        return true;
      } catch (error) {
        restoreDialog();
        return false;
      }
    }

    function navigate(direction) {
      const matching = filtered();
      if (selectedIndex < 0 || !matching.length) return;
      selectedIndex = (selectedIndex + direction + matching.length) % matching.length;
      updateDialog();
    }

    listen(dialog, 'close', restoreDialog);
    listen(dialog, 'click', (event) => { if (event.target === dialog) closeDialog(); });
    listen(dialog, 'keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        navigate(event.key === 'ArrowLeft' ? -1 : 1);
      }
    });

    const form = get('[data-request-form]');
    const draft = get('[data-request-draft]');
    const draftText = get('[data-draft-text]');
    const openEmail = get('[data-open-email]');
    const email = root.dataset.email;
    function prepareRequest(data) {
      const fields = [
        ['name', text.nameLabel],
        ['email', text.emailLabel],
        ['space', text.spaceLabel],
        ['budget', text.budgetLabel],
        ['dimensions', text.dimensionsLabel],
        ['requirements', text.requirementsLabel],
        ['reference', text.referenceLabel]
      ];
      return [
        text.requestTitle,
        '',
        ...fields.flatMap(([name, label]) => [label + ':', String(data.get(name) || '').trim() || text.notProvided, '']),
        text.attachReminder,
        '',
        text.consent
      ].join('\r\n');
    }

    listen(form, 'submit', (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      const body = prepareRequest(new FormData(form));
      const mailto = 'mailto:' + email + '?subject=' + encodeURIComponent(text.subject) + '&body=' + encodeURIComponent(body);
      draftText.value = body;
      openEmail.href = mailto;
      draft.hidden = false;
      get('[data-request-copy-status]').textContent = '';
      openEmail.focus({ preventScroll: true });
      draft.scrollIntoView({ block: 'nearest', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      window.location.href = mailto;
    });

    async function copyEmail() {
      const status = get('[data-email-copy-status]');
      try {
        await navigator.clipboard.writeText(email);
        status.textContent = text.copiedEmail;
      } catch (error) {
        status.textContent = text.copyEmailFallback;
        get('[data-contact-email]').focus();
      }
    }

    async function copyRequest() {
      const status = get('[data-request-copy-status]');
      try {
        await navigator.clipboard.writeText(draftText.value);
        status.textContent = text.copiedRequest;
      } catch (error) {
        status.textContent = text.copyRequestFallback;
        draftText.focus();
        draftText.select();
      }
    }

    listen(root, 'click', (event) => {
      const target = event.target.closest('button, a[data-photo-id]');
      if (!target || !root.contains(target)) return;
      if (target.matches('[data-category]')) {
        category = target.dataset.category;
        visibleCount = 6;
        renderGallery();
      } else if (target.matches('[data-gallery-more]')) {
        visibleCount += 9;
        renderGallery();
      } else if (target.matches('[data-photo-id]')) {
        if (openDialog(target)) event.preventDefault();
      } else if (target.matches('[data-dialog-close]')) {
        closeDialog();
      } else if (target.matches('[data-dialog-previous]')) {
        navigate(-1);
      } else if (target.matches('[data-dialog-next]')) {
        navigate(1);
      } else if (target.matches('[data-copy-email]')) {
        copyEmail();
      } else if (target.matches('[data-copy-request]')) {
        copyRequest();
      }
    });

    renderGallery();
    instances.set(root, () => {
      if (dialog.open) dialog.close();
      restoreDialog();
      controller.abort();
      instances.delete(root);
    });
  }

  function initializeWithin(target) {
    if (target.matches?.('[data-olcp]')) initialize(target);
    target.querySelectorAll('[data-olcp]').forEach(initialize);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  else initializeWithin(document);

  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-olcp]').forEach((root) => instances.get(root)?.());
  });
})();

